import { Position } from '@xyflow/react';
import { Coordinate, ExtendedNode } from '../components/types';

function getNodeIntersection(
  intersectionNode: ExtendedNode,
  targetNode: ExtendedNode
) {
  const { width: intersectionNodeWidth, height: intersectionNodeHeight } =
    intersectionNode.measured;
  const intersectionNodePosition = intersectionNode.internals.positionAbsolute;
  const targetPosition = targetNode.internals.positionAbsolute;

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x + targetNode.measured.width / 2;
  const y1 = targetPosition.y + targetNode.measured.height / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(node: ExtendedNode, intersectionPoint: Coordinate) {
  const n = { ...node.internals.positionAbsolute, ...node };
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + n.measured.width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + n.measured.height - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) to create an edge
export function getEdgeParams(source: ExtendedNode, target: ExtendedNode) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}

export function initialElements() {
  const nodes = [];
  const edges = [];
  const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  // Parent node at the center
  nodes.push({
    id: 'parent',
    data: { label: 'Lesson1: Introduction to the EU AI Act', expanded: true },
    position: center,
    type: 'custom',
  });

  const firstLayerRadius = 130; // Distance of first-layer nodes from the center
  const secondLayerDistance = 110; // Distance from child to sub-child
  const thirdLayerDistance = 100; // Distance from sub-child to sub-subchild

  const numChildren = 6;
  const numSubChildren = 3;

  for (let i = 0; i < numChildren; i++) {
    // Evenly distribute first-layer nodes in a circular layout
    const angle = (i * (2 * Math.PI)) / numChildren;
    const x = center.x + firstLayerRadius * Math.cos(angle);
    const y = center.y + firstLayerRadius * Math.sin(angle);

    const childId = `child-${i}`;

    nodes.push({
      id: childId,
      data: {
        label: `Child ${i + 1} Introduction`,
      },
      position: { x, y },
      type: 'custom',
    });

    edges.push({
      id: `edge-parent-${i}`,
      source: 'parent',
      target: childId,
      type: 'floating',
    });

    for (let j = 0; j < numSubChildren; j++) {
      // Space subchildren evenly around their parent
      const subAngle = angle + ((j - 1) * Math.PI) / 6; // Adjust spacing
      const subX = x + secondLayerDistance * Math.cos(subAngle);
      const subY = y + secondLayerDistance * Math.sin(subAngle);

      const subChildId = `subchild-${i}-${j}`;

      nodes.push({
        id: subChildId,
        data: { label: `Subchild ${i + 1}.${j + 1}`, expanded: false },
        parent: childId,
        position: { x: subX, y: subY },
        type: 'custom',
      });

      edges.push({
        id: `edge-child-${i}-${j}`,
        source: childId,
        target: subChildId,
        type: 'floating',
      });

      // Place sub-subchildren outward in the same direction
      const subSubAngle = subAngle + ((j - 1) * Math.PI) / 16;
      const subSubX = subX + thirdLayerDistance * Math.cos(subSubAngle);
      const subSubY = subY + thirdLayerDistance * Math.sin(subSubAngle);

      const subSubChildId = `subsubchild-${i}-${j}`;

      nodes.push({
        id: subSubChildId,
        data: {
          label: `SubSubchild ${i + 1}.${j + 1}.1 LoremLabore do ut nulla deserunt fugiat officia nisi reprehenderit. Id laborum proident et proident voluptate.`,
          expanded: false,
        },
        parent: subChildId,
        position: { x: subSubX, y: subSubY },
        type: 'custom',
      });

      edges.push({
        id: `edge-subchild-${i}-${j}`,
        source: subChildId,
        target: subSubChildId,
        type: 'floating',
      });
    }
  }

  return { nodes, edges };
}

export function generateMindMap(inputJson: any) {
  const nodes = [] as any[];
  const edges = [] as any[];
  const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  if (!inputJson || !inputJson.nodes) {
    return { nodes, edges };
  }

  // Parent node setup
  const parentNode = inputJson.nodes.find((node: any) => node.id === 'parent');
  if (!parentNode) {
    console.error('Parent node not found in the input JSON.');
    return { nodes, edges };
  }

  nodes.push({
    id: parentNode.id,
    data: { label: parentNode.data.label, expanded: true },
    position: center,
    type: 'custom',
  });

  // Layout distances
  const firstLayerRadius = 120;
  const secondLayerDistance = 110;
  const thirdLayerDistance = 160;

  // Get all children (3 to 6 children)
  const childNodes = inputJson.nodes.filter((node: any) =>
    node.id.startsWith('child-')
  );

  if (childNodes.length < 3 || childNodes.length > 6) {
    console.error('Children count should be between 3 to 6.');
    return { nodes, edges };
  }

  childNodes.forEach((child: any, i: number) => {
    // Calculate child node position around the parent
    const angle = (i * (2 * Math.PI)) / childNodes.length;
    const x = center.x + firstLayerRadius * Math.cos(angle);
    const y = center.y + firstLayerRadius * Math.sin(angle);

    nodes.push({
      id: child.id,
      data: { label: child.data.label },
      position: { x, y },
      type: 'custom',
    });

    edges.push({
      id: `edge-parent-${i}`,
      source: 'parent',
      target: child.id,
      type: 'floating',
    });

    // Get all sub-children for this child (1 to 3 sub-children)
    const subChildren = inputJson.nodes.filter((node: any) =>
      node.id.startsWith(`subchild-${i}-`)
    );

    if (subChildren.length < 1 || subChildren.length > 3) {
      console.error(
        `Subchildren count for ${child.id} should be between 1 to 3.`
      );
      return;
    }

    subChildren.forEach((subChild: any, j: number) => {
      // Calculate sub-child node position
      const subAngle =
        angle + ((j - (subChildren.length - 1) / 2) * Math.PI) / 6;
      const subX = x + secondLayerDistance * Math.cos(subAngle);
      const subY = y + secondLayerDistance * Math.sin(subAngle);

      nodes.push({
        id: subChild.id,
        data: { label: subChild.data.label, expanded: false },
        position: { x: subX, y: subY },
        type: 'custom',
      });

      edges.push({
        id: `edge-${child.id}-${subChild.id}`,
        source: child.id,
        target: subChild.id,
        type: 'floating',
      });

      // Get exactly one sub-subchild for this sub-child
      const subSubChild = inputJson.nodes.find((node: any) =>
        node.id.startsWith(`subsubchild-${i}-${j}`)
      );

      if (!subSubChild) {
        console.error(
          `Each subchild must have one sub-subchild. Missing for ${subChild.id}`
        );
        return;
      }

      // Calculate sub-subchild position
      const subSubX = subX + thirdLayerDistance * Math.cos(subAngle);
      const subSubY = subY + thirdLayerDistance * Math.sin(subAngle);

      nodes.push({
        id: subSubChild.id,
        data: { label: subSubChild.data.label, expanded: false },
        position: { x: subSubX, y: subSubY },
        type: 'custom',
      });

      edges.push({
        id: `edge-${subChild.id}-${subSubChild.id}`,
        source: subChild.id,
        target: subSubChild.id,
        type: 'floating',
      });
    });
  });

  return { nodes, edges };
}
